/* moire-bg-ribbons.js — tło canvas (wzór jak w bground.png) reagujące na scroll i mysz.
   Romby z równoległymi, przesuniętymi kopiami krawędzi ("wstęgi"). Scroll zmienia szerokość wstęg,
   a po zatrzymaniu wzór wraca do wyglądu z bground.png. Mysz zostawia kółka w kolorze tła. */
(() => {
  'use strict';

  // ---- ustawienia (wartości jak u LVMH) ----
  const BG = '#f7f5ef';          // kolor tła = rgb(247,245,239)
  const LINE = '#ece5d9';        // kolor linii
  const LINE_WIDTH = 3;          // w px canvasu
  const SCALE = 2;               // canvas ma 2x rozdzielczość
  const SPEED = 0.002;           // czułość wzoru na scroll (u LVMH 0.002 / 0.006 / 0.01 zależnie od urządzenia)
  const MAX_VEL = 3;
  const SETTLE = 0.03;           // powrót do rombów po zatrzymaniu scrolla
  const CIRCLE_START = 40, CIRCLE_MAX = 75;  // promień kółka (px canvasu), +-1 na klatkę
  const STAMP_EVERY = 25;        // ms między kółkami
  const MIN_WIDTH_CIRCLES = 760; // poniżej tej szerokości brak kółek
  const MOBILE_WIDTH = 600;      // poniżej tej szerokości mniejszy wzór
  const A_DESKTOP = 80, A_MOBILE = 40; // półszerokość rombu w px canvasu (wysokość = 2x tyle)
  const SWING = 1;               // 0..1: jak szeroko "oddychają" wstęgi przy scrollu (1 = od 0 do 2A)

  const clamp = (x, a, b) => Math.min(Math.max(x, a), b);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    const wrap = document.createElement('div');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = `position:fixed;inset:0;z-index:-1;pointer-events:none;background:${BG}`;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%';
    wrap.appendChild(canvas);
    document.body.prepend(wrap);
    const ctx = canvas.getContext('2d');

    let W, H, stepX, stepY, a, b;
    let m = 0.25, g = 0, drawnM = -1, dirty = true;
    let prevScroll = scrollY, prevTime = performance.now(), lastStamp = 0;
    const circles = [];

    function resize() {
      W = canvas.width = Math.round(wrap.clientWidth * SCALE);
      H = canvas.height = Math.round(wrap.clientHeight * SCALE);
      const mobile = innerWidth < MOBILE_WIDTH;
      a = mobile ? A_MOBILE : A_DESKTOP; b = a * 2;
      stepX = a * 4; stepY = b * 2;
      dirty = true;
    }

    // jeden element siatki w punkcie (0,0): romb + prawe i lewe kopie krawędzi przesunięte o d
    function motif() {
      // d = a w pozycji spoczynkowej (m = 0.25 / 0.75) = wygląd z bground.png
      const d = a * (1 + SWING * Math.sin(2 * Math.PI * (m - 0.25)));
      const p = new Path2D();
      p.moveTo(0, -b); p.lineTo(a, 0); p.lineTo(0, b); p.lineTo(-a, 0); p.closePath();
      p.moveTo(d, -b); p.lineTo(a + d, 0); p.lineTo(d, b);       // prawa wstęga
      p.moveTo(-d, -b); p.lineTo(-a - d, 0); p.lineTo(-d, b);    // lewa wstęga
      p.moveTo(a, 0); p.lineTo(a + d, 0);                        // poziome łączniki
      p.moveTo(-a, 0); p.lineTo(-a - d, 0);
      p.moveTo(-d, -b); p.lineTo(d, -b);
      p.moveTo(-d, b); p.lineTo(d, b);
      return p;
    }

    function render() {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.strokeStyle = LINE;
      ctx.lineWidth = LINE_WIDTH;
      const p = motif();
      for (let y = -stepY; y < H + stepY; y += stepY) {
        for (let x = -stepX; x < W + stepX; x += stepX) {
          ctx.setTransform(1, 0, 0, 1, x, y);
          ctx.stroke(p);
        }
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = BG;
      for (let i = circles.length - 1; i >= 0; i--) {
        const c = circles[i];
        ctx.beginPath(); ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2); ctx.fill();
        if (c.r >= CIRCLE_MAX) c.shrink = true;
        c.r = Math.max(0, c.shrink ? c.r - 1 : c.r + 1);
        if (c.shrink && c.r === 0) { circles.splice(i, 1); dirty = true; }
      }
      drawnM = m;
    }

    function update(now) {
      const dt = Math.max(now - prevTime, 1);
      const delta = scrollY - prevScroll;
      g += (clamp(delta / dt, -MAX_VEL, MAX_VEL) - g) * 0.2; // lekkie wygładzenie (u LVMH robi to Lenis)
      if (Math.abs(g) < 0.001) g = 0;
      m += SPEED * g;
      if (m > 1) m = 0; else if (m < 0) m = 1;
      if (delta === 0) {
        const rest = m < 0.5 ? 0.25 : 0.75;
        m += (rest - m) * SETTLE;
        if (Math.abs(rest - m) < 0.0005) m = rest;
      }
      prevScroll = scrollY; prevTime = now;
    }

    function frame(now) {
      requestAnimationFrame(frame);
      update(now);
      if (m !== drawnM || circles.length || dirty) { dirty = false; render(); }
    }

    let timer;
    addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(resize, 100); });

    if (!reduced) {
      addEventListener('pointermove', e => {
        if (innerWidth < MIN_WIDTH_CIRCLES || e.pointerType === 'touch') return;
        const now = performance.now();
        if (now - lastStamp < STAMP_EVERY) return;
        lastStamp = now;
        const r = canvas.getBoundingClientRect();
        circles.push({
          x: (e.clientX - r.left) / r.width * W,
          y: (e.clientY - r.top) / r.height * H,
          r: CIRCLE_START, shrink: false
        });
      }, { passive: true });
    }

    resize();
    if (reduced) render(); else requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') addEventListener('DOMContentLoaded', init);
  else init();
})();
